/**
 * ACIL Intelligence - Topbar v2.1 (Multi-Tenant)
 * Selector de cliente (solo Super Admin) + notificaciones + menú de usuario.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import {
  Bell, ChevronDown, User, LogOut, Settings,
  Building2, Globe, Search, Check, Layers, Menu,
} from 'lucide-react';

export default function Topbar({
  selectedRegion,
  onRegionChange,
  selectedBranch,
  onBranchChange,
  onMenuClick,
}) {
  const {
    user, logout, isSuperAdmin,
    activeClient, activeAccount,
    switchClient, switchAccount,
  } = useAuth();
  const navigate = useNavigate();

  const [showDropdown, setShowDropdown] = useState(false);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState('');
  const [mobileFilterMenu, setMobileFilterMenu] = useState(null);

  const dropdownRef = useRef(null);
  const clientSelectorRef = useRef(null);

  // Cargar alertas no leidas
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const { data } = await api.get('/api/alerts/unread-count');
        setAlertCount(data.count || 0);
      } catch { /* ignorar */ }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, []);

  // Cargar lista de clientes para selector (Super Admin + multi-client admins)
  // Multi-client admins reciben sus clientes desde user.accessible_clients (siempre actualizado en /me)
  const canSwitchClient = isSuperAdmin || ['account', 'clients'].includes(user?.access_mode);

  useEffect(() => {
    if (!canSwitchClient) return;
    const fetchData = async () => {
      try {
        if (isSuperAdmin) {
          // SA: cargar todos los clientes + cuentas para agrupación
          const [clientsRes, accountsRes] = await Promise.allSettled([
            api.get('/api/clientes/selector/lista'),
            api.get('/api/accounts'),
          ]);
          if (clientsRes.status === 'fulfilled') setClients(clientsRes.value.data || []);
          if (accountsRes.status === 'fulfilled') setAccounts(accountsRes.value.data?.items || []);
        } else {
          // Multi-client admin: consultar el endpoint del selector para no depender
          // de una copia stale/vacía de accessible_clients en la sesión.
          // Si el endpoint falla, conservar el perfil como fallback.
          try {
            const { data } = await api.get('/api/clientes/selector/lista');
            setClients(Array.isArray(data) ? data : (user?.accessible_clients || []));
          } catch {
            setClients(user?.accessible_clients || []);
          }
        }
      } catch { /* ignorar */ }
    };
    fetchData();
    // Recargar cuando cambia el cliente activo para que la lista esté siempre vigente
  }, [canSwitchClient, isSuperAdmin, user?.id, user?.client_id, user?.active_account_id, user?.accessible_clients]);

  // Cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (clientSelectorRef.current && !clientSelectorRef.current.contains(e.target)) {
        setShowClientSelector(false);
        setClientSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!user) return null;

  const level = user.role?.level || 1;
  const branches = user.branches || [];
  const initials = (user.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    setShowDropdown(false);
    await logout();
    navigate('/');
  };

  const handleSwitchClient = async (clientId) => {
    if (switching) return;
    if (clientId && activeClient?.id === clientId) {
      setShowClientSelector(false);
      return;
    }
    setSwitching(true);
    setSwitchError('');
    setShowClientSelector(false);
    setClientSearch('');
    try {
      await switchClient(clientId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let errMsg = 'Error al cambiar de contexto. Intenta de nuevo.';
      if (typeof detail === 'string') {
        if (detail.includes('permiso') || detail.includes('acceso')) {
          errMsg = 'No tienes permiso para cambiar a este cliente.';
        } else {
          errMsg = detail;
        }
      }
      setSwitchError(errMsg);
      console.error('[Topbar] switchClient error:', detail || err.message);
    } finally {
      setSwitching(false);
    }
  };

  const handleSwitchAccount = async (accountId) => {
    if (switching || !isSuperAdmin) return;
    if (accountId && activeAccount?.id === accountId) {
      setShowClientSelector(false);
      return;
    }
    setSwitching(true);
    setSwitchError('');
    setShowClientSelector(false);
    setClientSearch('');
    try {
      await switchAccount(accountId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSwitchError(typeof detail === 'string' ? detail : 'Error al cambiar de cuenta.');
      console.error('[Topbar] switchAccount error:', detail || err.message);
    } finally {
      setSwitching(false);
    }
  };

  const handleGlobalContext = async () => {
    if (switching) return;
    if (isSuperAdmin && activeAccount) {
      await handleSwitchAccount(null);
      return;
    }
    if (activeClient) {
      await handleSwitchClient(null);
      return;
    }
    setShowClientSelector(false);
  };

  const searchTerm = clientSearch.trim().toLowerCase();
  const filteredClients = clients.filter(c =>
    searchTerm === '' ||
    c.name.toLowerCase().includes(searchTerm) ||
    c.contract_number?.toLowerCase().includes(searchTerm)
  );

  const regions = [...new Set(
    branches.map(branch => branch.region).filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));

  const filteredBranches = selectedRegion && selectedRegion !== 'all'
    ? branches.filter(branch => branch.region === selectedRegion)
    : branches;

  const viewOptions = [];
  if (level >= 2) viewOptions.push({ value: 'nacional', label: 'Todas las sucursales' });

  const regionLabel = selectedRegion && selectedRegion !== 'all'
    ? selectedRegion
    : 'Todas las regiones';

  const branchOptions = [
    ...viewOptions.map(opt => ({ value: String(opt.value), label: opt.label })),
    ...filteredBranches.map(branch => ({ value: String(branch.id), label: branch.name })),
  ];

  const branchValue = String(selectedBranch || 'nacional');
  const branchLabel = branchOptions.find(opt => opt.value === branchValue)?.label || 'Todas las sucursales';

  const mobileFilterOptions = mobileFilterMenu === 'region'
    ? [
        { value: 'all', label: 'Todas las regiones' },
        ...regions.map(region => ({ value: region, label: region })),
      ]
    : branchOptions;

  const selectMobileFilterOption = (value) => {
    if (mobileFilterMenu === 'region') {
      onRegionChange?.(value);
    } else if (mobileFilterMenu === 'branch') {
      onBranchChange?.(value);
    }
    setMobileFilterMenu(null);
  };

  return (
    <>
    <div className="acil-topbar" data-testid="topbar">
      <button
        type="button"
        className="mobile-menu-btn"
        onClick={onMenuClick}
        aria-label="Abrir menú"
      >
        <Menu size={20} />
      </button>

      {/* Izquierda: Selector de cliente (SA y multi-client admins) o nombre de cliente fijo */}
      <div className="topbar-brand" data-testid="topbar-brand">
        {canSwitchClient ? (
          /* ── Selector de Cliente (Solo Super Admin) ── */
          <div ref={clientSelectorRef} style={{ position: 'relative' }}>
            <button
              className="client-selector-btn"
              onClick={() => { setSwitchError(''); setShowClientSelector(!showClientSelector); }}
              data-testid="client-selector-btn"
              disabled={switching}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: (activeClient || activeAccount) ? 'var(--orange-glow)' : 'transparent',
                border: `1.5px solid ${(activeClient || activeAccount) ? 'var(--orange-mid)' : 'var(--border)'}`,
                borderRadius: 8, padding: '6px 12px', cursor: switching ? 'wait' : 'pointer',
                color: 'var(--dark)', transition: 'all 0.2s', opacity: switching ? 0.7 : 1,
              }}
            >
              {switching ? (
                <>
                  <div className="acil-spinner acil-spinner-dark" style={{ width: 14, height: 14, borderWidth: 2, flexShrink: 0 }} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>Cambiando...</div>
                  </div>
                </>
              ) : activeClient ? (
                <>
                  <Building2 size={16} color="var(--orange)" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{activeClient.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{activeClient.contract_number}</div>
                  </div>
                </>
              ) : activeAccount ? (
                <>
                  <Layers size={16} color="var(--orange)" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{activeAccount.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {activeAccount.client_count ?? clients.length} contratos · todas sus sucursales
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Globe size={16} color="var(--muted)" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                      {isSuperAdmin ? 'Vista Global ACIL' : 'Vista Cuenta'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {isSuperAdmin ? 'Todos los clientes' : `${clients.length} contratos`}
                    </div>
                  </div>
                </>
              )}
              {!switching && <ChevronDown size={14} color="var(--muted)" style={{ marginLeft: 4 }} />}
            </button>

            {/* Error de switch */}
            {switchError && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#DC2626', whiteSpace: 'nowrap', zIndex: 1001 }}>
                {switchError}
              </div>
            )}

            {showClientSelector && (
              <div
                data-testid="client-selector-dropdown"
                style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: 6,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                  width: 280, zIndex: 1000, overflow: 'hidden',
                }}
              >
                {/* Busqueda */}
                <div style={{ padding: 10, borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg)', borderRadius: 6, padding: '6px 10px' }}>
                    <Search size={14} color="var(--muted)" />
                    <input
                      type="text"
                      placeholder="Buscar cuenta o cliente..."
                      value={clientSearch}
                      onChange={e => setClientSearch(e.target.value)}
                      data-testid="client-search-input"
                      className="client-search-input"
                      style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: 'var(--dark)', flex: 1 }}
                    />
                  </div>
                </div>

                {/* Vista Global */}
                <button
                  onClick={handleGlobalContext}
                  data-testid="client-selector-global"
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', background: (!activeClient && !activeAccount) ? 'var(--orange-glow)' : 'transparent',
                    border: 'none', cursor: 'pointer', color: 'var(--dark)',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <Globe size={16} color="var(--muted)" />
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {isSuperAdmin ? 'Vista Global ACIL' : 'Vista Cuenta'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {isSuperAdmin ? 'Todos los clientes' : 'Todos mis contratos'}
                    </div>
                  </div>
                  {!activeClient && !activeAccount && <Check size={14} color="var(--orange)" />}
                </button>

                {/* Lista de clientes — agrupada por Cuenta si existen cuentas */}
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {accounts.length > 0 ? (
                    /* ── Modo agrupado ── */
                    (() => {
                      // Agrupar clientes filtrados por account_id
                      const grouped = {};
                      const unassigned = [];
                      filteredClients.forEach(c => {
                        if (c.account_id) {
                          if (!grouped[c.account_id]) grouped[c.account_id] = [];
                          grouped[c.account_id].push(c);
                        } else {
                          unassigned.push(c);
                        }
                      });

                      return (
                        <>
                          {accounts.map(acc => {
                            const accountMatchesSearch = searchTerm === ''
                              || String(acc.name || '').toLowerCase().includes(searchTerm);
                            const accClients = accountMatchesSearch
                              ? clients.filter(c => String(c.account_id || '') === String(acc.id))
                              : (grouped[acc.id] || []);
                            if (accClients.length === 0) return null;
                            return (
                              <div key={acc.id}>
                                {/* Cuenta/Empresa completa: todas sus regiones y sucursales */}
                                <button
                                  type="button"
                                  onClick={() => handleSwitchAccount(acc.id)}
                                  data-testid={`account-option-${acc.id}`}
                                  style={{
                                    width: '100%',
                                    display: 'flex', alignItems: 'center', gap: 7,
                                    padding: '8px 14px',
                                    fontSize: 11, fontWeight: 800, letterSpacing: '0.04em',
                                    color: 'var(--orange)', textTransform: 'uppercase',
                                    background: activeAccount?.id === acc.id ? 'var(--orange-glow)' : 'var(--bg)',
                                    border: 'none',
                                    borderTop: '1px solid var(--border)',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                  }}
                                >
                                  <Layers size={12} />
                                  <span style={{ flex: 1 }}>{acc.name}</span>
                                  <span style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'none', letterSpacing: 0 }}>
                                    {acc.client_count ?? accClients.length} contratos
                                  </span>
                                  {activeAccount?.id === acc.id && <Check size={13} color="var(--orange)" />}
                                </button>
                                {accClients.map(c => (
                                  <button
                                    key={c.id}
                                    onClick={() => handleSwitchClient(c.id)}
                                    data-testid={`client-option-${c.id}`}
                                    style={{
                                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                                      padding: '8px 14px 8px 26px',
                                      background: activeClient?.id === c.id ? 'var(--orange-glow)' : 'transparent',
                                      border: 'none', cursor: 'pointer', color: 'var(--dark)',
                                    }}
                                  >
                                    <Building2 size={14} color="var(--orange)" />
                                    <div style={{ textAlign: 'left', flex: 1 }}>
                                      <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                                        {c.contract_number}{c.mysql_contrato ? ` · ${c.mysql_contrato}` : ''}
                                      </div>
                                    </div>
                                    {activeClient?.id === c.id && <Check size={13} color="var(--orange)" />}
                                  </button>
                                ))}
                              </div>
                            );
                          })}
                          {unassigned.length > 0 && (
                            <div>
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '7px 14px 4px',
                                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                                color: 'var(--muted)', textTransform: 'uppercase',
                                background: 'var(--bg)',
                                borderTop: '1px solid var(--border)',
                                userSelect: 'none',
                              }}>
                                <Layers size={11} />
                                Sin cuenta
                              </div>
                              {unassigned.map(c => (
                                <button
                                  key={c.id}
                                  onClick={() => handleSwitchClient(c.id)}
                                  data-testid={`client-option-${c.id}`}
                                  style={{
                                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '8px 14px 8px 26px',
                                    background: activeClient?.id === c.id ? 'var(--orange-glow)' : 'transparent',
                                    border: 'none', cursor: 'pointer', color: 'var(--dark)',
                                  }}
                                >
                                  <Building2 size={14} color="var(--muted)" />
                                  <div style={{ textAlign: 'left', flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                                      {c.contract_number}{c.mysql_contrato ? ` · ${c.mysql_contrato}` : ''}
                                    </div>
                                  </div>
                                  {activeClient?.id === c.id && <Check size={13} color="var(--orange)" />}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    /* ── Fallback: lista plana (sin cuentas o error de carga) ── */
                    filteredClients.length > 0 ? filteredClients.map(c => (
                      <button
                        key={c.id}
                        onClick={() => handleSwitchClient(c.id)}
                        data-testid={`client-option-${c.id}`}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 14px',
                          background: activeClient?.id === c.id ? 'var(--orange-glow)' : 'transparent',
                          border: 'none', cursor: 'pointer', color: 'var(--dark)',
                        }}
                      >
                        <Building2 size={16} color="var(--orange)" />
                        <div style={{ textAlign: 'left', flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.contract_number}</div>
                        </div>
                        {activeClient?.id === c.id && <Check size={14} color="var(--orange)" />}
                      </button>
                    )) : (
                      <div style={{ padding: '14px', fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
                        No hay contratos disponibles en esta cuenta
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── Nombre fijo del cliente (otros roles) ── */
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Building2 size={16} color="var(--orange)" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--dark)' }}>
                {activeClient?.name || user?.active_client?.name || 'ACIL Intelligence'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{user.role?.name}</div>
            </div>
          </div>
        )}
      </div>

      {/* Centro: jerarquía Cuenta/Cliente → Región → Sucursal */}
      <div className="topbar-selector" data-testid="topbar-branch-selector" style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
        <div className="desktop-filter-controls">
          {isSuperAdmin && !activeClient && !activeAccount ? (
            <select disabled data-testid="branch-select" style={{ opacity: 0.55, cursor: 'not-allowed' }}>
              <option>Vista Global — Selecciona una cuenta o cliente</option>
            </select>
          ) : (
            <>
              <select
                value={selectedRegion || 'all'}
                onChange={(e) => onRegionChange?.(e.target.value)}
                data-testid="region-select"
                title="Región"
              >
                <option value="all">Todas las regiones</option>
                {regions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
              <select
                value={selectedBranch || 'nacional'}
                onChange={(e) => onBranchChange?.(e.target.value)}
                data-testid="branch-select"
                title="Sucursal"
              >
                {viewOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
                {filteredBranches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            </>
          )}
        </div>

        <div className="mobile-filter-controls" data-testid="mobile-filter-controls">
          {isSuperAdmin && !activeClient && !activeAccount ? (
            <button type="button" className="mobile-filter-trigger mobile-filter-trigger-full" disabled>
              <span>Selecciona una cuenta o cliente</span>
            </button>
          ) : (
            <>
              <button
                type="button"
                className="mobile-filter-trigger"
                onClick={() => setMobileFilterMenu(prev => prev === 'region' ? null : 'region')}
                data-testid="mobile-region-trigger"
              >
                <span>{regionLabel}</span>
                <ChevronDown size={16} />
              </button>
              <button
                type="button"
                className="mobile-filter-trigger"
                onClick={() => setMobileFilterMenu(prev => prev === 'branch' ? null : 'branch')}
                data-testid="mobile-branch-trigger"
              >
                <span>{branchLabel}</span>
                <ChevronDown size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Derecha: Notificaciones + Usuario */}
      <div className="topbar-actions">
        <button
          className="topbar-notif-btn"
          onClick={() => navigate('/alertas')}
          data-testid="notifications-btn"
        >
          <Bell size={18} />
          {alertCount > 0 && (
            <span className="topbar-notif-badge" data-testid="alert-count-badge">
              {alertCount > 99 ? '99+' : alertCount}
            </span>
          )}
        </button>

        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <div
            className="topbar-user"
            onClick={() => setShowDropdown(!showDropdown)}
            data-testid="user-menu-trigger"
          >
            <div className="topbar-user-avatar">{initials}</div>
            <div className="topbar-user-info">
              <div className="topbar-user-name">{user.name}</div>
              <div className="topbar-user-role">{user.role?.name}</div>
            </div>
            <ChevronDown size={16} color="var(--muted)" />
          </div>

          {showDropdown && (
            <div className="topbar-dropdown" data-testid="user-dropdown">
              <button
                className="topbar-dropdown-item"
                onClick={() => { navigate('/perfil'); setShowDropdown(false); }}
                data-testid="dropdown-profile-btn"
              >
                <User size={16} /> Mi perfil
              </button>
              {level >= 4 && (
                <button
                  className="topbar-dropdown-item"
                  onClick={() => { navigate('/configuracion'); setShowDropdown(false); }}
                  data-testid="dropdown-settings-btn"
                >
                  <Settings size={16} /> Configuración
                </button>
              )}
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
              <button
                className="topbar-dropdown-item danger"
                onClick={handleLogout}
                data-testid="dropdown-logout-btn"
              >
                <LogOut size={16} /> Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </div>

    {mobileFilterMenu && !(isSuperAdmin && !activeClient && !activeAccount) && (
      <div className="mobile-filter-modal" data-testid="mobile-filter-modal">
        <button
          type="button"
          className="mobile-filter-backdrop"
          aria-label="Cerrar selector"
          onClick={() => setMobileFilterMenu(null)}
        />
        <div
          className="mobile-filter-sheet"
          role="dialog"
          aria-modal="true"
          aria-label={mobileFilterMenu === 'region' ? 'Seleccionar región' : 'Seleccionar sucursal'}
        >
          <div className="mobile-filter-sheet-header">
            <strong>{mobileFilterMenu === 'region' ? 'Seleccionar región' : 'Seleccionar sucursal'}</strong>
            <button type="button" className="btn-ghost" onClick={() => setMobileFilterMenu(null)}>
              Cerrar
            </button>
          </div>
          <div className="mobile-filter-options">
            {mobileFilterOptions.map(option => {
              const selected = mobileFilterMenu === 'region'
                ? String(selectedRegion || 'all') === String(option.value)
                : branchValue === String(option.value);

              return (
                <button
                  type="button"
                  key={`${mobileFilterMenu}-${option.value}`}
                  className={`mobile-filter-option${selected ? ' selected' : ''}`}
                  onClick={() => selectMobileFilterOption(option.value)}
                >
                  <span>{option.label}</span>
                  {selected && <Check size={17} />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
